import { useEvent } from '@shared-core/hooks'
import { StatusBarGradient } from '@shared-ui/components/ui/screen-wrappers/status-bar-gradient'
import { useScrollRestoration } from '@shared-ui/hooks/use-scroll-restoration'
import { ScrollRestorationOverlay } from '@shared-ui/hooks/use-scroll-restoration/overlay'
import { useAppTheme } from '@shared-ui/theme'
import {
  type ComponentRef,
  cloneElement,
  forwardRef,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  useState,
} from 'react'
import {
  RefreshControl,
  type RefreshControlProps,
  type ScrollViewProps,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native'
import {
  KeyboardAvoidingView,
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewProps,
} from 'react-native-keyboard-controller'
import { useCreateStyles } from './styles'

const webScrollProps = { dataSet: { screenScroll: '' } }

type RefreshViewProps = PropsWithChildren<RefreshControlProps>

export function RefreshView(props: RefreshViewProps) {
  const { children, style, refreshing, onRefresh } = props
  const { COLOR_TEXT_PRIMARY, COLOR_BG_BASE } = useAppTheme()

  // ScrollView on web wraps refreshControl around itself and copies scroll styles onto it.
  // RefreshControl is a plain View there, so we flatten the wrapper to keep a single scroll container.
  if (isValidElement(children)) {
    const scrollView = children as ReactElement<{ style?: ViewStyle | ViewStyle[] }>

    return cloneElement(scrollView, {
      style: [{ zIndex: 1 }, style, scrollView.props.style] as ViewStyle[],
    })
  }

  return (
    <RefreshControl
      style={{ zIndex: 1 }}
      progressViewOffset={1}
      tintColor={COLOR_TEXT_PRIMARY}
      titleColor={COLOR_TEXT_PRIMARY}
      colors={[COLOR_TEXT_PRIMARY]}
      progressBackgroundColor={COLOR_BG_BASE}
      refreshing={refreshing}
      onRefresh={onRefresh}
    >
      {children}
    </RefreshControl>
  )
}

type ScreenWrapperProps = ScrollViewProps &
  KeyboardAwareScrollViewProps & {
    isDisableStatusBar?: boolean
    originalViewStyle?: ViewStyle
    bottomOffset?: number
    isTransparent?: boolean
    isDisableSafeTop?: boolean
    // id области скролла для восстановления позиции при возврате (web); задавать при >1 скролле на экране.
    scrollRestorationId?: string
  }

// Web: внутренний скролл (KeyboardAwareScrollView). Позиция восстанавливается точечно через
// useScrollRestoration (ключ истории кормит web-adapter), а не глобальным <ScrollRestoration>.
export const ScreenWrapper = forwardRef<ComponentRef<typeof KeyboardAwareScrollView>, ScreenWrapperProps>(
  function ScreenWrapperComponent(props, ref) {
    const {
      style,
      originalViewStyle,
      children,
      isDisableStatusBar,
      bottomOffset,
      isTransparent,
      isDisableSafeTop,
      contentContainerStyle,
      testID,
      scrollRestorationId = 'main',
      ...otherProps
    } = props
    const theme = useAppTheme()
    const styles = useCreateStyles()
    const [isVisibleStatusBackground, setIsVisibleStatusBackground] = useState(false)
    const {
      ref: scrollRef,
      onScroll: onRestoreScroll,
      isRestoring,
    } = useScrollRestoration<ComponentRef<typeof KeyboardAwareScrollView>>(scrollRestorationId, ref)
    const handleScroll = useEvent<NonNullable<ScrollViewProps['onScroll']>>((event) => {
      setIsVisibleStatusBackground(event.nativeEvent.contentOffset.y > 20)

      onRestoreScroll(event)

      props.onScroll?.(event)
    })
    const viewStyle: ViewStyle = {
      flex: 1,
      minHeight: 0,
      backgroundColor: isTransparent ? 'transparent' : theme.COLOR_BG_BASE,
    }

    return (
      <>
        {isDisableStatusBar !== true && <StatusBarGradient isVisible={isVisibleStatusBackground} />}
        <View style={viewStyle}>
          <KeyboardAwareScrollView
            {...webScrollProps}
            ref={scrollRef}
            testID={testID}
            bottomOffset={50}
            showsVerticalScrollIndicator={false}
            {...otherProps}
            style={[viewStyle, originalViewStyle]}
            onScroll={handleScroll as ScrollViewProps['onScroll']}
            scrollEventThrottle={16}
            contentContainerStyle={[
              styles.wrapper,
              isTransparent && styles.wrapperTransparent,
              isDisableSafeTop && styles.wrapperWithoutSafeTop,
              style,
              contentContainerStyle,
              bottomOffset !== undefined && { paddingBottom: bottomOffset },
            ]}
          >
            {children}
          </KeyboardAwareScrollView>
          <ScrollRestorationOverlay isVisible={isRestoring} />
        </View>
      </>
    )
  },
)

type ScreenWrapperWithoutScrollProps = ViewProps & {
  enabled?: boolean
}

export function ScreenWrapperWithoutScroll(props: ScreenWrapperWithoutScrollProps) {
  const { children, style, ...rest } = props
  const styles = useCreateStyles()

  const viewStyle: ViewStyle = {
    flex: 1,
    minHeight: 0,
  }

  return (
    <KeyboardAvoidingView
      keyboardVerticalOffset={-20}
      behavior="padding"
      style={viewStyle}
      {...rest}
      enabled={props.enabled}
    >
      <View style={[styles.wrapperWithoutScroll, style]}>{children}</View>
    </KeyboardAvoidingView>
  )
}
